import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { cn } from "@shared/utils";
import { Button, IconButton } from "../atoms";

interface DateRangeValue {
  start: Date | null;
  end: Date | null;
}

interface DateRangePickerProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  onClear: () => void;
  onApply: () => void;
  onClose?: () => void;
}

/**
 * Date range picker component.
 * Allows selection of start and end dates with month/year navigation.
 * Dimensions: 375×550px. Border: primary-110, radius: 16px.
 */
export function DateRangePicker({
  value,
  onChange,
  onClear,
  onApply,
  onClose,
}: DateRangePickerProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
  const containerRef = useRef<HTMLDivElement>(null);

  const monthNames = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  const today = new Date();

  const handleDateClick = (day: number) => {
    const newDate = new Date(currentYear, currentMonth, day);

    if (!value.start || (value.start && value.end)) {
      onChange({ start: newDate, end: null });
    } else if (newDate < value.start) {
      onChange({ start: newDate, end: null });
    } else {
      onChange({ start: value.start, end: newDate });
    }
  };

  const daysInMonth = (month: number, year: number) =>
    new Date(year, month + 1, 0).getDate();

  const firstDayOfMonth = (month: number, year: number) =>
    new Date(year, month, 1).getDay();

  const days = Array.from(
    { length: daysInMonth(currentMonth, currentYear) },
    (_, i) => i + 1,
  );

  const firstDay = firstDayOfMonth(currentMonth, currentYear);
  const emptyDays = Array.from({ length: firstDay }, (_, i) => i);

  const isDayPassed = (day: number) => {
    const date = new Date(currentYear, currentMonth, day);
    return (
      date < new Date(today.getFullYear(), today.getMonth(), today.getDate())
    );
  };

  const isDayInRange = (day: number) => {
    if (!value.start || !value.end) return false;
    const date = new Date(currentYear, currentMonth, day);
    return date > value.start && date < value.end;
  };

  const isDayStart = (day: number) => {
    if (!value.start) return false;
    const date = new Date(currentYear, currentMonth, day);
    return (
      date.getDate() === value.start.getDate() &&
      date.getMonth() === value.start.getMonth() &&
      date.getFullYear() === value.start.getFullYear()
    );
  };

  const isDayEnd = (day: number) => {
    if (!value.end) return false;
    const date = new Date(currentYear, currentMonth, day);
    return (
      date.getDate() === value.end.getDate() &&
      date.getMonth() === value.end.getMonth() &&
      date.getFullYear() === value.end.getFullYear()
    );
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setShowMonthDropdown(false);
        setShowYearDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="border border-primary-110 absolute top-full left-0 z-20 mt-2 bg-white rounded-2xl shadow-lg p-4">
      <div className="flex justify-between items-center mb-4 px-2">
        <h4 className="text-primary-110 font-semibold">Fecha</h4>

        <IconButton onClick={onClose}>
          <X size={20} className="text-primary-110" />
        </IconButton>
      </div>
      <div
        ref={containerRef}
        className="rounded-[16px] bg-white  w-[280px] space-y-3"
      >
        {/* Month/Year header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <IconButton
              onClick={() => {
                if (currentMonth === 0) {
                  setCurrentMonth(11);
                  setCurrentYear(currentYear - 1);
                } else setCurrentMonth(currentMonth - 1);
              }}
            >
              <ChevronLeft size={20} className="text-primary-110" />
            </IconButton>

            <div className="relative ">
              <button
                onClick={() => setShowMonthDropdown(!showMonthDropdown)}
                className="flex  gap-3 text-primary-110 font-semibold text-body cursor-pointer hover:opacity-70 transition-opacity cursor-pointer"
              >
                {monthNames[currentMonth]}
                <img
                  src="/svg/icons/caret-down.svg"
                  alt="caret"
                  className="w-2.5 h-2.5"
                />
              </button>

              {showMonthDropdown && (
                <div className="absolute top-full left-0 z-50 mt-2 bg-white  rounded-lg shadow-lg max-h-48 overflow-y-auto w-32">
                  {monthNames.map((month, idx) => (
                    <button
                      key={month}
                      onClick={() => {
                        setCurrentMonth(idx);
                        setShowMonthDropdown(false);
                      }}
                      className={cn(
                        "w-full px-4 py-2 text-left text-body hover:bg-primary-10 cursor-pointer",
                        idx === currentMonth
                          ? "bg-primary-10 font-semibold"
                          : "",
                      )}
                    >
                      {month}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <IconButton
              onClick={() => {
                if (currentMonth === 11) {
                  setCurrentMonth(0);
                  setCurrentYear(currentYear + 1);
                } else setCurrentMonth(currentMonth + 1);
              }}
            >
              <ChevronRight size={20} className="text-primary-110" />
            </IconButton>
          </div>

          <div className="flex items-center gap-2">
            <IconButton onClick={() => setCurrentYear(currentYear - 1)}>
              <ChevronLeft size={20} className="text-primary-110" />
            </IconButton>

            <div className="relative">
              <button
                onClick={() => setShowYearDropdown(!showYearDropdown)}
                className="flex gap-3 text-primary-110 font-semibold text-body cursor-pointer hover:opacity-70 transition-opacity"
              >
                {currentYear}
                <img
                  src="/svg/icons/caret-down.svg"
                  alt="caret"
                  className="w-2.5 h-2.5"
                />
              </button>
              {showYearDropdown && (
                <div className="absolute top-full left-0 z-50 mt-2 bg-white rounded-lg shadow-lg max-h-48 overflow-y-auto w-24">
                  {years.map((year) => (
                    <button
                      key={year}
                      onClick={() => {
                        setCurrentYear(year);
                        setShowYearDropdown(false);
                      }}
                      className={cn(
                        "w-full px-4 py-2 text-left text-body hover:bg-primary-10",
                        year === currentYear
                          ? "bg-primary-10 font-semibold"
                          : "",
                      )}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <IconButton onClick={() => setCurrentYear(currentYear + 1)}>
              <ChevronRight size={20} className="text-primary-110" />
            </IconButton>
          </div>
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 ">
          {/* Day headers */}
          {["L", "M", "M", "J", "V", "S", "D"].map((day) => (
            <div
              key={day}
              className="text-center text-xs font-semibold text-primary-110 py-2"
            >
              {day}
            </div>
          ))}

          {/* Empty days */}
          {emptyDays.map((i) => (
            <div key={`empty-${i}`} />
          ))}

          {/* Calendar days */}
          {days.map((day) => {
            const isStart = isDayStart(day);
            const isEnd = isDayEnd(day);
            const inRange = isDayInRange(day);
            const passed = isDayPassed(day);

            return (
              <button
                key={day}
                disabled={passed}
                onClick={() => handleDateClick(day)}
                className="relative w-full h-10 flex items-center justify-center cursor-pointer "
              >
                {/* Fondo del rango */}
                {inRange && (
                  <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-8 bg-primary-110/20" />
                )}
                {/* Extensión desde START */}
                {isStart && value.end && (
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 h-8 w-1/2 bg-primary-110/20" />
                )}
                {/* Extensión hacia END */}
                {isEnd && value.start && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 h-8 w-1/2 bg-primary-110/20" />
                )}
                <span
                  className={cn(
                    "relative z-10 flex items-center justify-center w-8 h-8 text-sm font-semibold",
                    isStart || isEnd
                      ? "bg-primary-110 text-white rounded-full"
                      : inRange
                        ? "text-primary-110"
                        : passed
                          ? "text-neutral-40"
                          : "text-primary-110",
                  )}
                >
                  {day}
                </span>
              </button>
            );
          })}
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 pt-4">
          <Button
            className="w-[135px] border border-primary-110 bg-white text-primary-110 hover:bg-primary-10"
            onClick={() => {
              onChange({ start: null, end: null });
              onClear();
            }}
          >
            Limpiar
          </Button>

          <Button
            className="w-[135px] border border-primary-110 bg-secondary-120 hover:bg-secondary-120/90"
            onClick={onApply}
          >
            Aplicar
          </Button>
        </div>
      </div>
    </div>
  );
}
